import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, errMsg, newIdemKey, type Counterparty, type CounterpartyPage } from "../../api";
import { HistoryModal } from "../../components/HistoryModal";
import { Modal } from "../../components/Modal";
import { Btn, Field, Input, Notice, Select } from "../../components/ui";
import { useEntities } from "../../entity";
import { useMe } from "../../session";

const fmtDoc = (d: string | null) => !d ? "—" : d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
const PAGE = 25;
type Role = "cliente" | "fornecedor" | "ambos";
const ROLE_SHORT: Record<Role, string> = { cliente: "Pagador", fornecedor: "Beneficiário", ambos: "Pagador e beneficiário" };
const ROLE_HELP: Record<Role, string> = { cliente: "Pagador — quem me paga (aparece nas receitas)", fornecedor: "Beneficiário — quem eu pago (aparece nas despesas)", ambos: "Os dois (aparece nas receitas e nas despesas)" };

/** Beneficiários e pagadores (no sistema: contrapartes): pessoas ou organizações com quem se transaciona (distintas das entidades que possuem as contas). */
export function Counterparties() {
  const { me } = useMe();
  const { entities } = useEntities();
  const realm = me.user.realm;
  const canWrite = entities.some((e) => e.isOwner || e.permission === "total");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | "ativa" | "inativa">("ativa");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<CounterpartyPage | null>(null);
  const [form, setForm] = useState<Counterparty | "new" | null>(null);
  const [hist, setHist] = useState<Counterparty | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) });
    if (q.trim()) p.set("q", q.trim());
    if (status) p.set("status", status);
    return api<CounterpartyPage>("GET", `/api/${realm}/counterparties?${p}`).then(setData).catch((e) => setMsg({ kind: "error", text: errMsg(e) }));
  }, [realm, q, status, page]);
  useEffect(() => { const t = setTimeout(() => void load(), 200); return () => clearTimeout(t); }, [load]);

  async function act(fn: () => Promise<unknown>, ok: string) {
    try { await fn(); setMsg({ kind: "ok", text: ok }); setForm(null); await load(); }
    catch (e) { setMsg({ kind: "error", text: errMsg(e) }); await load(); }
  }

  return (
    <div className="card" data-counterparties>
      <h3 style={{ fontSize: 16, marginBottom: 4 }}>Beneficiários e Pagadores</h3>
      <p className="muted" style={{ marginTop: 0 }}>Quem recebe o seu pagamento (beneficiário, nas despesas) e quem paga você (pagador, nas receitas). Um mesmo cadastro serve para receitas e despesas{realm === "pj" ? " de todas as empresas do ambiente" : ""}. Inativar mantém todo o histórico.</p>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <div className="row" style={{ flexWrap: "wrap", margin: "8px 0 12px" }}>
        <Input placeholder="Buscar por nome ou documento" aria-label="Buscar beneficiário ou pagador" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} style={{ maxWidth: 280 }} name="cpSearch" />
        <Select aria-label="Situação" value={status} onChange={(e) => { setStatus(e.target.value as typeof status); setPage(0); }} style={{ maxWidth: 160 }} name="cpStatus">
          <option value="ativa">Ativas</option><option value="inativa">Inativas</option><option value="">Todas</option>
        </Select>
        {canWrite && <Btn onClick={() => { setMsg(null); setForm("new"); }}>+ Novo beneficiário/pagador</Btn>}
      </div>
      {data && data.items.length === 0 && <p className="muted" data-empty>Nenhum beneficiário ou pagador encontrado.</p>}
      {data && data.items.length > 0 && (
        <div className="table-card"><table>
          <thead><tr><th>Nome</th><th>Documento</th><th>Tipo</th><th>Situação</th><th /></tr></thead>
          <tbody>{data.items.map((c) => (
            <tr key={c.id} className={c.status === "inativa" ? "inactive" : ""} data-cp={c.name}>
              <td><strong>{c.name}</strong></td><td>{fmtDoc(c.document)}</td><td data-cp-role={c.role ?? "ambos"}>{ROLE_SHORT[c.role ?? "ambos"]}</td>
              <td><span className={`pill ${c.status === "ativa" ? "accent" : ""}`}>{c.status === "ativa" ? "Ativa" : "Inativa"}</span></td>
              <td className="num">
                {canWrite && c.status === "ativa" && <button className="link" onClick={() => { setMsg(null); setForm(c); }}>Editar</button>}
                {canWrite && (c.status === "ativa"
                  ? <button className="link danger" onClick={() => void act(() => api("POST", `/api/${realm}/counterparties/${c.id}/inactivate`, { version: c.version }), `"${c.name}" inativada.`)}>Inativar</button>
                  : <button className="link" onClick={() => void act(() => api("POST", `/api/${realm}/counterparties/${c.id}/reactivate`, { version: c.version }), `"${c.name}" reativada.`)}>Reativar</button>)}
                <button className="link" onClick={() => setHist(c)}>Histórico</button>
              </td>
            </tr>))}</tbody>
        </table></div>
      )}
      {data && data.total > PAGE && (
        <div className="row" style={{ marginTop: 10 }}>
          <Btn variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>Anterior</Btn>
          <span className="muted small">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, data.total)} de {data.total}</span>
          <Btn variant="ghost" disabled={(page + 1) * PAGE >= data.total} onClick={() => setPage(page + 1)}>Próxima</Btn>
        </div>
      )}
      {form && <CpModal initial={form === "new" ? undefined : form} onClose={() => setForm(null)}
        onSave={(name, document, role) => form === "new"
          ? act(async () => { const n = await api<Counterparty>("POST", `/api/${realm}/counterparties`, { name, document }, newIdemKey()); if (role !== "ambos") await api("PUT", `/api/${realm}/partners/${n.id}/role`, { role }); }, `"${name}" cadastrada.`)
          : act(async () => { await api("PATCH", `/api/${realm}/counterparties/${form.id}`, { version: form.version, name, document }); if (role !== (form.role ?? "ambos")) await api("PUT", `/api/${realm}/partners/${form.id}/role`, { role }); }, "Cadastro atualizado.")} />}
      {hist && <HistoryModal title={`Histórico — ${hist.name}`} path={`/api/${realm}/counterparties/${hist.id}/history`} onClose={() => setHist(null)} />}
    </div>
  );
}

function CpModal({ initial, onSave, onClose }: { initial?: Counterparty; onSave: (name: string, doc: string | null, role: Role) => Promise<void>; onClose: () => void }) {
  const [role, setRole] = useState<Role>(initial?.role ?? "ambos");
  const [name, setName] = useState(initial?.name ?? "");
  const [doc, setDoc] = useState(initial?.document ?? "");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) { e.preventDefault(); setBusy(true); try { await onSave(name.trim(), doc.replace(/\D/g, "") || null, role); } finally { setBusy(false); } }
  return (
    <Modal title={initial ? "Editar beneficiário/pagador" : "Novo beneficiário/pagador"} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <Field label="Nome"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required name="cpName" /></Field>
        <Field label="CPF ou CNPJ (opcional, somente números)"><Input value={doc} onChange={(e) => setDoc(e.target.value)} inputMode="numeric" name="cpDocument" /></Field>
        <Field label="Tipo"><Select value={role} onChange={(e) => setRole(e.target.value as Role)} name="cpRole" data-cp-role-select>{(Object.keys(ROLE_HELP) as Role[]).map((r) => <option key={r} value={r}>{ROLE_HELP[r]}</option>)}</Select></Field>
        <div className="row"><button type="submit" className="btn primary" disabled={busy || !name.trim()}>{busy ? "Salvando…" : "Salvar"}</button><Btn variant="ghost" onClick={onClose}>Cancelar</Btn></div>
      </form>
    </Modal>
  );
}
